import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Ho4ArticleSidebar from '../components/Ho4ArticleSidebar';
import { HO4_LAST_MODIFIED } from '../data/ho4Articles';

function Ho4NoInsuranceTexasArticlePage() {
  const navigate = useNavigate();
  const articleLinkStyle = { color: '#065F46', fontWeight: 600, textDecoration: 'underline' };

  useEffect(() => {
    const previousTitle = document.title;
    const descriptionMeta = document.querySelector('meta[name="description"]');
    const previousDescription = descriptionMeta?.getAttribute('content') ?? '';

    document.title = 'What Happens If You Don\'t Have Renters Insurance in Texas? | GetSkimp';

    if (descriptionMeta) {
      descriptionMeta.setAttribute(
        'content',
        'Learn what happens if you skip renters insurance in Texas — lease violations, out-of-pocket loss risks, liability exposure, and when landlords can require HO-4.'
      );
    }

    return () => {
      document.title = previousTitle;
      if (descriptionMeta) descriptionMeta.setAttribute('content', previousDescription);
    };
  }, []);

  return (
    <div className="container">
      <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: '2rem' }}>
        <Ho4ArticleSidebar
          activeHref="/knowledge-base/what-happens-if-you-dont-have-renters-insurance-in-texas"
          onNavigate={(href) => navigate(href)}
        />
        <section>
          <div style={{ marginBottom: '1rem' }}>
            <button type="button" className="btn btn-sm btn-outline" onClick={() => navigate('/knowledge-base')} style={{ marginBottom: '1rem' }}>
              Back to self help library
            </button>
            <h2 style={{ marginBottom: '0.5rem' }}>What happens if you don&apos;t have renters insurance in Texas?</h2>
            <p style={{ marginBottom: 0, color: '#4B5563' }}>
              Texas does not require renters insurance by state law, but going without coverage exposes you to financial
              risk and possible lease violations. Here is what you could face.
            </p>
            <p style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: '#6B7280' }}>Last modified: {HO4_LAST_MODIFIED}</p>
          </div>

          <article className="card mb-4">
            <div className="card-body">
              <h3 style={{ marginBottom: '0.75rem' }}>Quick answer</h3>
              <p>
                Without renters insurance in Texas, you pay out of pocket to replace stolen or damaged belongings, cover
                liability if you injure someone or damage property, and potentially face <strong>lease violations</strong>{' '}
                if your landlord requires HO-4. A typical HO-4 policy costs far less than a single uninsured loss — see{' '}
                <Link to="/knowledge-base/how-much-does-ho4-cost-in-texas" style={articleLinkStyle}>HO-4 costs in Texas</Link>.
              </p>

              <h3 style={{ marginBottom: '0.75rem', marginTop: '1.5rem' }}>Is renters insurance required in Texas?</h3>
              <p>
                Texas state law does not mandate renters insurance for all tenants. However,{' '}
                <strong>your lease can require it</strong> — and most large apartment communities in Texas do. If your
                lease includes an insurance clause and you do not comply, that is a contract violation regardless of
                state law. Read{' '}
                <Link to="/knowledge-base/is-ho4-required-by-landlord" style={articleLinkStyle}>is HO-4 required by my landlord</Link>{' '}
                for lease-specific guidance.
              </p>

              <h3 style={{ marginBottom: '0.75rem', marginTop: '1.5rem' }}>Consequences of not having coverage</h3>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.95rem' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Risk</th>
                      <th style={{ textAlign: 'left', borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Without HO-4</th>
                      <th style={{ textAlign: 'left', borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>With HO-4</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Stolen or fire-damaged belongings</td>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Replace everything yourself</td>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Personal property coverage helps pay</td>
                    </tr>
                    <tr>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Accidental damage to landlord&apos;s unit</td>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Pay repair costs or face lawsuit</td>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Liability coverage may pay</td>
                    </tr>
                    <tr>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Guest injury in your apartment</td>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Personal assets at risk</td>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Liability + legal defense</td>
                    </tr>
                    <tr>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Forced out by covered disaster</td>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Pay hotel and meals yourself</td>
                      <td style={{ borderBottom: '1px solid var(--color-grey)', padding: '0.6rem' }}>Loss of use / ALE may help</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '0.6rem' }}>Lease requires insurance</td>
                      <td style={{ padding: '0.6rem' }}>Violation fees, non-renewal, or eviction risk</td>
                      <td style={{ padding: '0.6rem' }}>Compliant with lease terms</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3 style={{ marginBottom: '0.75rem', marginTop: '1.5rem' }}>Real costs of going uninsured</h3>
              <ul style={{ paddingLeft: '1.2rem', marginBottom: 0 }}>
                <li style={{ marginBottom: '0.5rem' }}>
                  <strong>Theft:</strong> A single burglary can cost $3,000–$10,000+ in electronics, clothing, and gear.
                </li>
                <li style={{ marginBottom: '0.5rem' }}>
                  <strong>Fire or water damage:</strong> Replacing all belongings after a total loss can exceed $20,000–$40,000.
                </li>
                <li style={{ marginBottom: '0.5rem' }}>
                  <strong>Liability:</strong> A guest injury or apartment fire spreading to neighbors can lead to lawsuits
                  exceeding $100,000 — without coverage, your savings and future wages are exposed.
                </li>
                <li>
                  <strong>Lease penalties:</strong> Some Texas landlords charge monthly fees ($10–$15+) for non-compliance
                  or require forced-place insurance at a higher cost.
                </li>
              </ul>

              <h3 style={{ marginBottom: '0.75rem', marginTop: '1.5rem' }}>Common myths about skipping renters insurance</h3>
              <ul style={{ paddingLeft: '1.2rem', marginBottom: 0 }}>
                <li style={{ marginBottom: '0.5rem' }}>
                  <strong>&quot;My landlord&apos;s insurance covers my stuff.&quot;</strong> False — it covers the building, not your belongings.
                </li>
                <li style={{ marginBottom: '0.5rem' }}>
                  <strong>&quot;I don&apos;t own enough to insure.&quot;</strong> Most renters underestimate — clothing and electronics alone often total $15,000+.
                </li>
                <li style={{ marginBottom: '0.5rem' }}>
                  <strong>&quot;It is too expensive.&quot;</strong> HO-4 in Texas often runs $15–$30/month — less than many streaming subscriptions.
                </li>
                <li>
                  <strong>&quot;My parents&apos; homeowners policy covers me.&quot;</strong> Maybe, but often with limits — see{' '}
                  <Link to="/knowledge-base/ho4-for-college-students-in-texas" style={articleLinkStyle}>HO-4 for college students in Texas</Link>.
                </li>
              </ul>

              <h3 style={{ marginBottom: '0.75rem', marginTop: '1.5rem' }}>How to get covered quickly</h3>
              <p style={{ marginBottom: 0 }}>
                If your lease requires proof before move-in, you can often bind a policy the same day. Follow{' '}
                <Link to="/knowledge-base/how-to-get-ho4-renters-insurance" style={articleLinkStyle}>how to get HO-4</Link>, then{' '}
                <Link to="/knowledge-base/how-to-get-proof-of-renters-insurance-for-landlord" style={articleLinkStyle}>get proof for your landlord</Link>.
                Still deciding? Read{' '}
                <Link to="/knowledge-base/is-ho4-worth-it" style={articleLinkStyle}>is HO-4 worth it</Link>.
              </p>
            </div>
          </article>

          <section className="card mb-4" style={{ borderColor: 'var(--color-green)', background: 'linear-gradient(135deg, #ECFDF5 0%, #FFFFFF 100%)', boxShadow: 'var(--shadow-md)' }}>
            <div className="card-body">
              <h3 style={{ marginBottom: '0.5rem', color: '#065F46' }}>Start renters insurance with GetSkimp</h3>
              <p style={{ marginBottom: '1rem' }}>Affordable HO-4 coverage in Texas — protect your belongings and liability starting today.</p>
              <button type="button" className="btn btn-primary" onClick={() => navigate('/qualify')} style={{ fontWeight: 700, paddingInline: '1.4rem' }}>Get started now</button>
            </div>
          </section>
          <section className="card" style={{ borderColor: '#FBBF24', background: 'linear-gradient(135deg, #FFFBEB 0%, #FFFFFF 100%)' }}>
            <div className="card-body">
              <h3 style={{ marginBottom: '0.5rem', color: '#92400E' }}>Still on the fence?</h3>
              <p style={{ marginBottom: '1rem' }}>Talk to an agent about what you would actually pay vs. what you would risk without coverage.</p>
              <button type="button" className="btn btn-outline" onClick={() => navigate('/help')} style={{ borderColor: '#D97706', color: '#92400E', fontWeight: 700 }}>Speak to an agent</button>
            </div>
          </section>
        </section>
      </div>
    </div>
  );
}

export default Ho4NoInsuranceTexasArticlePage;
